import { Button, Stack } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import Divider from "components/Divider/Divider";
import {
  pickTheFieldsFrom,
  CONFIG_KEYS,
  filterDifferences,
} from "../utils";
import { useTranslation } from "react-i18next";
import { updateMerchant, updateMerchantOtherInfo } from "../model";
import { useSnackbar } from "notistack";
import ConfigSection from "../../add-offline-merchant/Component/ConfigSection";
import useFormUndo from "utils/useFormUndo/useFormUndo";
import { formPreventEnterSubmit } from "utils/utils";
import ButtonMAS from "ButtonMAS/ButtonMAS";

const PAGE_LOCALE_KEY = "page.merchants.";

const MerchantConfig = ({
  merchant,
  merchantOtherDetail,
  fetchMerchantAndMerchantOtherDetail,
  staticMerchantPaylaod,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const formProps = useForm({
    mode: "onChange",
  });
  const { handleSubmit, reset } = formProps;
  const { rememberForm, undoForm, clearFormMemory } = useFormUndo({ formProps });

  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  const onSubmit = async (formValue) => {
    try {
      setIsLoading(true);

      const MERCHANT_UPDATE_IGNORE_FIELDS = [
        "settleCycle",
        "otherInfo",
        "zeroPlan",
      ];
      const MERCHANT_OTHER_INFO_UPDATE_FIELDS = ["settleCycle", "zeroPlan"];
      const MERCHANT_OTHER_INFO_UPDATE_IGNORE_FIELDS = ["otherInfo"];

      const apiConfigs = [
        {
          key: "MERCHANT",
          diff: filterDifferences(
            merchant,
            formValue,
            [],
            MERCHANT_UPDATE_IGNORE_FIELDS,
          ),
          payload: {
            ...staticMerchantPaylaod(merchant, "config", {
              previewUrl: formValue.merchantLogo,
            }),
            ...filterDifferences(
              merchant,
              formValue,
              [],
              MERCHANT_UPDATE_IGNORE_FIELDS,
            ),
          },
          apiCall: updateMerchant,
        },
        {
          key: "MERCHANT_OTHER_INFO",
          diff: filterDifferences(
            merchantOtherDetail,
            formValue,
            MERCHANT_OTHER_INFO_UPDATE_FIELDS,
            MERCHANT_OTHER_INFO_UPDATE_IGNORE_FIELDS,
          ),
          payload: {
            ...filterDifferences(
              merchantOtherDetail,
              formValue,
              MERCHANT_OTHER_INFO_UPDATE_FIELDS,
              MERCHANT_OTHER_INFO_UPDATE_IGNORE_FIELDS,
            ),
            merchantId: merchant?.merchantId,
            id: merchantOtherDetail?.id,
            // ...(merchantOtherDetail?.sales?.id && {
            //   sales: { id: merchantOtherDetail.sales.id },
            // }),
            // ...(merchantOtherDetail?.onboardSales?.id && {
            //   onboardSales: { id: merchantOtherDetail.onboardSales.id },
            // }),
            // ...(merchantOtherDetail?.referrer?.id && {
            //   referrer: { id: merchantOtherDetail.referrer.id },
            // }),
          },
          apiCall: updateMerchantOtherInfo,
        },
      ];
      const validApis = apiConfigs.filter((config) => {
        console.log(`${config.key} config.diff`, config.diff);
        return Object.keys(config.diff).length > 0;
      });
      if (validApis.length === 0) {
        snackbar(
          t(PAGE_LOCALE_KEY + "merchantDetail.fetchMessage.noChanges"),
          "info",
        );
        return;
      }

      const [responses, errors] = await Promise.allSettled(
        validApis.map((config) => config.apiCall(config.payload)),
      ).then((results) => {
        const res = {};
        const errs = {};

        results.forEach((result, index) => {
          console.log("result", result);
          const key = validApis[index].key;
          if (result.status === "fulfilled") {
            res[key] = result.value;
          } else {
            errs[key] = result.reason;
            console.error(`${key} error:`, result.reason);
          }
        });

        return [res, errs];
      });

      let hasError = false;

      validApis.forEach(({ key }) => {
        const response = responses[key];
        const error = errors[key];
        if (error) {
          hasError = true;
          return snackbar(
            `${t(PAGE_LOCALE_KEY + "merchantDetail.fetchMessage.updateFailed")}: ${error.message}`,
            "error",
          );
        }

        if (response?.status === 401) {
          console.log("Unauthorized access");
          hasError = true;
          return logout();
        }

        if (response?.status === 403) {
          hasError = true;
          return snackbar(t("general.message.unauthorized"), "error");
        }

        const respBody = response?.data;
        if (respBody?.responseCode !== "0000") {
          hasError = true;
          return snackbar(
            `${t(PAGE_LOCALE_KEY + "merchantDetail.fetchMessage.updateFailed")}: ${error?.message || "Unknown error"
            }`,
            "error",
          );
        }
      });

      if (!hasError && validApis.length > 0) {
        snackbar(
          t(PAGE_LOCALE_KEY + "merchantDetail.fetchMessage.updateSuccess"),
          "success",
        );
      }
      await fetchMerchantAndMerchantOtherDetail();
    } catch (error) {
      console.error("Submission error:", error);
      snackbar(
        `${t(PAGE_LOCALE_KEY + "merchantDetail.fetchMessage.updateFailed")}: ${error.message}`,
        "error",
      );
    } finally {
      setIsEditing(false);
      setIsLoading(false);
      clearFormMemory();
    }
  };
  const appendBase64Data = (base64String) => {
    if (base64String?.startsWith("iVBORw0KGgo")) {
      base64String = "data:image/png;base64," + base64String;
    }
    if (base64String?.startsWith("/9j/")) {
      base64String = "data:image/jpeg;base64," + base64String;
    }
    return base64String;
  };

  useEffect(() => {
    if (Object.keys(merchant).length > 0) {
      // console.log("merchant", merchant);
      const initialValue = {
        ...pickTheFieldsFrom(merchant, CONFIG_KEYS()),
        ...pickTheFieldsFrom(merchantOtherDetail, CONFIG_KEYS()),
      };
      setAllValues(initialValue);
    }
  }, [merchant]);

  const setAllValues = (initialValue) => {
    reset(
      {
        ...initialValue,
        isKDollar: !!initialValue?.otherInfo?.campaign?.find(
          (campaign) => campaign === "KDOLLAR",
        ),
        merchantLogo: appendBase64Data(initialValue?.merchantLogo),
      },
      {
        keepDefaultValues: false,
        keepDirty: false,
        keepErrors: false,
      },
    );
  };

  return (
    <Stack direction="column" sx={{ padding: "24px", background: "white" }}>
      <form noValidate onSubmit={handleSubmit(onSubmit)} onKeyDown={formPreventEnterSubmit}>
        <ConfigSection formProps={formProps} isEdit={isEditing} isLoading={isLoading} />
        <Divider style={{ width: "80%", margin: "24px 0" }} />
        <Stack
          direction={"row"}
          gap={2}
          flexWrap={"wrap"}
          justifyContent={"space-between"}
        >
          {/* <Button
            variant="normal-button"
            onClick={() => {
              const path = "/transactions/offline-transactions";
              const payload = {
                name: transactions.find((tab) => tab.path === path).tabName,
                path: path,
                state: {
                  merchantId: merchant?.merchantId,
                },
              };
              redirectFn("redirectPage", payload);
            }}
            disabled={loading}
          >
            View Transaction
          </Button> */}
          <div></div>
          <Stack direction={"row"} gap={2} flexWrap={"wrap"}>
            {!isEditing ? (
              <Button
                variant="normal-button"
                onClick={() => {
                  setIsEditing(true);
                  rememberForm()
                }}
                disabled={isLoading}
              >
                {t(`${PAGE_LOCALE_KEY}merchantDetail.button.edit`)}
              </Button>
            ) : (
              <>
                <Button
                  variant="outline-normal-button"
                  onClick={() => {
                    setIsEditing(false);
                    undoForm();
                  }}
                  disabled={isLoading}
                >
                  {t(`${PAGE_LOCALE_KEY}merchantDetail.button.cancel`)}
                </Button>
                <ButtonMAS
                  type="submit"
                  isLoading={isLoading}
                  disabled={isLoading}
                >
                  {t(`${PAGE_LOCALE_KEY}merchantDetail.button.save`)}
                </ButtonMAS>
              </>
            )}
          </Stack>
        </Stack>
      </form>
    </Stack>
  );
};

export default React.memo(MerchantConfig);
